import React, { forwardRef, useCallback, Children, useState, useEffect, useMemo, useRef, isValidElement } from 'react';
import { createMemoStyles, safeRest, defaultBaseline, uniqueId, KEYS } from '@wds/utils';
import { Pane } from '@wds/internal-components';
import { IconCheck, IconError, IconInfoFilled, IconWarningFilled } from '@wds/icons';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function () {
  __assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
function __rest(s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}
function __spreadArray(to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
}
var useStyles$1 = createMemoStyles(function (theme) {
  var spacing = theme.spacing,
    radius = theme.radius,
    palette = theme.palette,
    type = theme.type;
  var tab = {
    borderRadius: radius.zero,
    indicatorHeight: 3,
    indicatorColor: palette.primary.main,
    typeColor: palette.text.darkMuted,
    fontSize: type.fontSize.bodySm,
    fontWeight: type.fontWeight.medium,
    paddingY: spacing[3],
    paddingX: 2
  };
  var alignTab = function (align) {
    var alignmentMap = {
      left: {
        paddingLeft: spacing[6],
        '&:last-child': {
          paddingRight: spacing[6]
        }
      },
      right: {
        paddingRight: spacing[6],
        paddingLeft: 0,
        '&:first-child': {
          paddingLeft: spacing[6]
        }
      },
      even: {
        width: '100%',
        paddingLeft: 0,
        paddingRight: 0,
        grow: 1,
        overflow: 'hidden',
        textAlign: 'center'
      },
      center: {
        display: 'inline-block',
        padding: [0, spacing[3]]
      }
    };
    return alignmentMap[align];
  };
  var colorTabBtn = function (color, selected) {
    var buttonColorMap = {
      light: {
        color: selected ? tab.indicatorColor : palette.text.dark,
        borderBottom: selected ? "".concat(tab.indicatorHeight, "px solid ").concat(tab.indicatorColor) : 'none'
      },
      dark: {
        color: palette.text.light,
        borderBottom: selected ? "".concat(tab.indicatorHeight, "px solid ").concat(palette.text.light) : 'none'
      },
      transparent: {
        color: selected ? tab.indicatorColor : palette.text.dark,
        borderBottom: selected ? "".concat(tab.indicatorHeight, "px solid ").concat(tab.indicatorColor) : 'none'
      }
    };
    return buttonColorMap[color];
  };
  return {
    wrenchTabIcon: {
      display: 'inline',
      paddingLeft: spacing[2]
    },
    wrenchTab: function (_a) {
      var align = _a.align;
      return __assign({
        boxSizing: 'border-box',
        margin: 0,
        backgroundColor: 'transparent'
      }, alignTab(align));
    },
    wrenchTabBtn: function (_a) {
      var color = _a.color,
        selected = _a.selected,
        disabled = _a.disabled;
      return __assign(__assign({
        display: 'block',
        background: 'transparent',
        border: 'none',
        borderRadius: 0,
        padding: "".concat(tab.paddingY, "px ").concat(tab.paddingX, "px"),
        fontSize: tab.fontSize,
        fontWeight: tab.fontWeight,
        fontFamily: type.fontFamily.base,
        '-webkit-font-smoothing': 'antialiased',
        cursor: 'pointer',
        width: '100%',
        lineHeight: 1.5,
        whiteSpace: 'nowrap',
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto'
      }, colorTabBtn(color, selected)), {
        '&:hover, &:focus': {
          textDecoration: 'none'
        }
      });
    }
  };
});
var TabIcon = function (_a) {
  var indicator = _a.indicator,
    wrenchTabIcon = _a.wrenchTabIcon;
  return React.createElement("div", {
    className: wrenchTabIcon
  }, {
    success: React.createElement(IconCheck, {
      title: "TabIndicator",
      color: "success"
    }),
    error: React.createElement(IconError, {
      title: "TabIndicator",
      color: "error"
    }),
    info: React.createElement(IconInfoFilled, {
      title: "TabIndicator",
      color: "info"
    }),
    warning: React.createElement(IconWarningFilled, {
      title: "TabIndicator",
      color: "warning"
    })
  }[indicator]);
};
var Tab = forwardRef(function (_a, ref) {
  var _b = _a.selected,
    selected = _b === void 0 ? false : _b,
    _c = _a.disabled,
    disabled = _c === void 0 ? false : _c,
    id = _a.id,
    _d = _a.align,
    align = _d === void 0 ? "left" : _d,
    _e = _a.color,
    color = _e === void 0 ? "light" : _e,
    indicator = _a.indicator,
    _f = _a.value,
    value = _f === void 0 ? 0 : _f,
    onKeyDown = _a.onKeyDown,
    onClick = _a.onClick,
    children = _a.children,
    rest = __rest(_a, ["selected", "disabled", "id", "align", "color", "indicator", "value", "onKeyDown", "onClick", "children"]);
  var _g = useStyles$1({
      selected: selected,
      disabled: disabled,
      align: align,
      color: color
    }),
    wrenchTab = _g.wrenchTab,
    wrenchTabBtn = _g.wrenchTabBtn,
    wrenchTabIcon = _g.wrenchTabIcon;
  var handleClick = function (evt) {
    onClick(evt, evt.target, value, id);
  };
  var handleKeyPress = function (evt) {
    if (onKeyDown) {
      onKeyDown(evt, id);
    }
  };
  return React.createElement("li", __assign({
    role: "presentation",
    className: wrenchTab
  }, safeRest(rest)), React.createElement("button", {
    onKeyDown: handleKeyPress,
    type: "button",
    role: "tab",
    "aria-selected": selected,
    "aria-disabled": disabled,
    disabled: disabled,
    value: value,
    tabIndex: disabled || !selected ? -1 : 0,
    onClick: handleClick,
    className: wrenchTabBtn,
    id: id,
    ref: ref
  }, children, indicator && React.createElement(TabIcon, {
    indicator: indicator,
    wrenchTabIcon: wrenchTabIcon
  })));
});
var useStyles = createMemoStyles(function (theme) {
  var border = theme.border,
    radius = theme.radius,
    palette = theme.palette;
  var alignTabs = function (align) {
    return {
      left: {
        justifyContent: 'flex-start'
      },
      right: {
        justifyContent: 'flex-end'
      },
      even: {
        justifyContent: 'center'
      },
      center: {
        whiteSpace: 'nowrap',
        textAlign: 'center'
      }
    }[align];
  };
  var colorTabs = function (color) {
    return {
      light: {
        background: palette.background.light
      },
      dark: {
        background: palette.background.dark
      },
      transparent: {
        background: 'transparent',
        borderBottom: border.main
      }
    }[color];
  };
  return {
    wrenchTabs: function (_a) {
      var align = _a.align,
        color = _a.color;
      return __assign(__assign(__assign(__assign({}, defaultBaseline), {
        // if align is set to center
        display: align === 'center' ? 'block' : 'flex',
        flexDirection: 'row',
        alignContent: 'flex-start',
        overflowX: 'auto',
        borderRadius: radius.zero
      }), alignTabs(align)), colorTabs(color));
    },
    wrenchTabPanes: {}
  };
});
var Tabs = function (_a) {
  var _b = _a.align,
    align = _b === void 0 ? "left" : _b,
    _c = _a.color,
    color = _c === void 0 ? "light" : _c,
    _d = _a.fullWidth,
    fullWidth = _d === void 0 ? false : _d,
    _e = _a.selectionFollowsFocus,
    selectionFollowsFocus = _e === void 0 ? true : _e,
    onChange = _a.onChange,
    children = _a.children,
    rest = __rest(_a, ["align", "color", "fullWidth", "selectionFollowsFocus", "onChange", "children"]);
  // Finds the first selected tab that is not set to disabled
  var getSelectedValue = useCallback(function (prevSelected) {
    if (prevSelected === void 0) {
      prevSelected = -1;
    }
    var selected = prevSelected;
    Children.forEach(children, function (child, index) {
      var _a, _b, _c;
      if (((_b = (_a = child) === null || _a === void 0 ? void 0 : _a.props) === null || _b === void 0 ? void 0 : _b.selected) && !child.props.disabled) {
        selected = (_c = child.props.value) !== null && _c !== void 0 ? _c : index;
      }
    });
    return selected;
  }, [children]);
  // Initializes selectedValue with either null or first tab that has selected=true
  var _f = useState(getSelectedValue),
    selectedValue = _f[0],
    setSelectedValue = _f[1];
  // When controlled selected value changes in children, this will change selected value
  useEffect(function () {
    setSelectedValue(getSelectedValue);
  }, [getSelectedValue]);
  // Creates a uniqueID for this Tabs component
  var uniqueTabName = useMemo(function () {
    return uniqueId('wds-tab-');
  }, []);
  var tabListRef = useRef(null);
  var tabRef = useRef(null);
  var paneRef = useRef(null);
  var _g = useStyles({
      color: color,
      align: align
    }),
    wrenchTabPanes = _g.wrenchTabPanes,
    wrenchTabs = _g.wrenchTabs;
  var handleChange = function (evt, newFocusTarget, value, id, select) {
    if (newFocusTarget !== null) {
      //  This allows for selectionFollowsFocus case
      if (select || evt.type === 'click' || selectionFollowsFocus) {
        setSelectedValue(value);
      }
      newFocusTarget.focus();
      evt.preventDefault();
      if (onChange) {
        onChange(evt, newFocusTarget, value, id);
      }
    }
  };
  var handleKeyPress = function (evt, id) {
    var key = evt.key,
      target = evt.target;
    var btn = target;
    var role = btn.getAttribute('role');
    if (role !== 'tab' || !Object.values(KEYS).includes(key)) {
      return;
    }
    // This will create an array of tab button elements and filter out any disabled tabs
    var tabListFlat = __spreadArray([], tabListRef.current ? [].slice.call(tabListRef.current.children) : [], true).map(function (tab) {
      return tab.firstChild;
    }).filter(function (button) {
      return !button.disabled;
    });
    var tabListLength = tabListFlat.length - 1;
    var tabIndex = tabListFlat.indexOf(btn);
    var newFocusTarget = null;
    var select = false;
    switch (evt.key) {
      // Handles when TAB is pressed
      case KEYS.TAB:
        if (paneRef.current) {
          paneRef.current.focus();
          evt.preventDefault();
        }
        return;
      // When RIGHT or DOWN is pressed to goto the next focused tab
      case KEYS.RIGHT:
      case KEYS.DOWN:
        newFocusTarget = tabIndex < tabListLength ? tabListFlat[tabIndex + 1] : tabListFlat[0];
        break;
      // When LEFT or UP is pressed to goto the previous focused tab
      case KEYS.LEFT:
      case KEYS.UP:
        newFocusTarget = tabIndex !== 0 ? tabListFlat[tabIndex - 1] : tabListFlat[tabListLength];
        break;
      // When HOME is pressed to goto the first tab
      case KEYS.HOME:
        newFocusTarget = tabListFlat[0];
        break;
      // When END is pressed to goto the last tab
      case KEYS.END:
        newFocusTarget = tabListFlat[tabListLength];
        break;
      case KEYS.SPACE:
      case KEYS.ENTER:
        newFocusTarget = btn;
        select = true;
        break;
    }
    if (newFocusTarget !== null) {
      handleChange(evt, newFocusTarget, parseInt(newFocusTarget.value, 10), id, select);
    }
  };
  var renderTabElement = function (child, value, selected, id) {
    var _a = child.props,
      title = _a.title,
      tabProps = __rest(_a, ["title"]);
    return React.createElement(Tab, __assign({}, tabProps, {
      ref: selected ? tabRef : undefined,
      key: value,
      id: id,
      selected: selected,
      align: align,
      color: color,
      value: value,
      onKeyDown: handleKeyPress,
      onClick: handleChange
    }), title);
  };
  var renderPaneElement = function (paneChildren, value, selected, id) {
    var paneProps = fullWidth ? {
      transparent: true
    } : {
      padding: true
    };
    return React.createElement("section", {
      ref: selected ? paneRef : undefined,
      key: value,
      role: "tabpanel",
      tabIndex: 0,
      "aria-labelledby": id,
      "aria-hidden": !selected
    }, React.createElement(Pane, __assign({}, paneProps, {
      hide: !selected
    }), paneChildren));
  };
  var tabList = [];
  var paneList = [];
  Children.forEach(children, function (child, index) {
    var _a, _b;
    if (!isValidElement(child)) {
      return;
    }
    var id = (_a = child.props.id) !== null && _a !== void 0 ? _a : "".concat(uniqueTabName, "-").concat(index);
    var childValue = (_b = child.props.value) !== null && _b !== void 0 ? _b : index;
    var selected = childValue === selectedValue;
    tabList.push(renderTabElement(child, childValue, selected, id));
    if (child.props.children) {
      paneList.push(renderPaneElement(child.props.children, childValue, selected, id));
    }
  });
  return React.createElement("div", null, React.createElement("ul", __assign({
    role: "tablist",
    ref: tabListRef,
    className: wrenchTabs
  }, safeRest(rest)), tabList), React.createElement("div", {
    className: wrenchTabPanes
  }, paneList));
};
var TabDummy = function (props) {
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return React.createElement(React.Fragment, null);
};
var TAB_COLORS = ['light', 'dark', 'transparent'];
var TAB_ALIGN = ['left', 'right', 'even', 'center'];
var TAB_INDICATORS = ['warning', 'error', 'success', 'info'];
export { TAB_ALIGN, TAB_COLORS, TAB_INDICATORS, TabDummy as Tab, Tabs };